<template>
    <v-stepper v-model="viewData.currentStep">
        <v-stepper-header>
            <v-stepper-step v-for="item in availableSteps" :key="item.value" :step="item.value">
                {{ item.title }}
            </v-stepper-step>
        </v-stepper-header>
        <v-stepper-items ref="stepperItems">
            <v-stepper-content :step="steps.flightData.value">
                <FlightData
                    :airlines="airlines"
                    @validity-changed="onFlightDataValidityChange"
                    @form-data-changed="onFlightDataChange"
                />
            </v-stepper-content>
            <v-stepper-content :step="steps.additionalData.value">
                <AdditionalData
                    :case-type="selectedCaseType"
                    :has-stopover="hasStopover"
                    @validity-changed="onAdditionalDataValidityChange"
                    @form-data-changed="onAdditionalDataChange"
                    @has-rsv-value-changed="onHasRSVValueChanged"
                />
            </v-stepper-content>
            <v-stepper-content :step="steps.fulfillmentSelection.value">
                <FulfillmentSelection
                    :compensation-result="formData.calculatedCompensation"
                    :has-r-s-v="formData.hasRSV"
                    :airlines="airlines"
                    :flight-data="formData.flightData"
                    @validity-changed="onFulfillmentSelectionValidityChange"
                    @form-data-changed="onFulfillmentSelectionChange"
                />
            </v-stepper-content>
            <v-stepper-content v-if="this.formData.selectedFulfillmentMethod === FulfillmentMethods.SELF.value" :step="steps.personalData.value">
                <PersonalData
                    @validity-changed="onPersonalDataValidityChange"
                    @form-data-changed="onPersonalDataChange"
                    :has-r-s-v="hasRSV"
                    :rsvs="rsvs"
                />
            </v-stepper-content>
            <v-stepper-content v-if="this.formData.selectedFulfillmentMethod === FulfillmentMethods.SELF.value" :step="steps.summaryPage.value">
                <SummaryPage
                    :form-data="formData"
                    :airlines="airlines"
                    :rsvs="rsvs"
                    @validity-changed="onSummaryValidityChange"
                />
            </v-stepper-content>
            <v-stepper-content v-if="this.formData.selectedFulfillmentMethod !== FulfillmentMethods.SELF.value" :step="steps.redirectionPage.value">
                <RedirectionPage :data-id="formData.portalDataId"/>
            </v-stepper-content>
        </v-stepper-items>
        <v-card>
            <v-card-actions>
                <v-btn min-width="150" rounded class="secondary" @click.stop="stepBackward" :disabled="isBackStepperDisabled">Zurück</v-btn>
                <v-spacer/>
                <v-btn min-width="150" rounded class="primary" @click.stop="stepForward" :disabled="isForwardStepperDisabled">{{stepperNextText}}</v-btn>
            </v-card-actions>
        </v-card>
        <ErrorDialog v-model="viewData.showErrorDialog" :error-details="errorDetails" :is-closable="viewData.isErrorDialogClosable"/>
        <SubmitDialog :is-loading="viewData.isLoading" v-model="viewData.showSubmitDialog"/>
    </v-stepper>
</template>

<script>
import FlightData from "@/components/FlightForm/FlightData.vue";
import FulfillmentSelection from "@/components/FlightForm/FulfillmentSelection.vue";
import AdditionalData from "@/components/FlightForm/AdditionalData.vue";
import PersonalData from "@/components/FlightForm/PersonalData.vue";
import {calculateCompensation, createNewCase, createUnfinishedCase, getAirlines, getRsvs} from "@/api/flights";
import FulfillmentMethods from "@/constants/FulfillmentMethods";
import RedirectionPage from "@/components/FlightForm/RedirectionPage.vue";
import moment from "moment";
import CaseTypes from "@/constants/CaseTypes";
import CauseTypes from "@/constants/CauseTypes";
import CompensationScenarios from "@/constants/CompensationScenarios";
import RadioButtonStringValues from "@/constants/RadioButtonStringValues";
import ErrorDialog from "@/components/FlightForm/ErrorDialog.vue";
import Config from "@/constants/Config";
import {checkHealth} from "@/api/request";
import SummaryPage from "@/components/FlightForm/SummaryPage.vue";
import SubmitDialog from "@/components/FlightForm/SubmitDialog.vue";

export default {
    name: 'FlightForm',
    components: {
        SubmitDialog,
        SummaryPage,
        ErrorDialog,
        RedirectionPage,
        PersonalData,
        AdditionalData,
        FulfillmentSelection,
        FlightData
    },
    computed: {
        FulfillmentMethods() {
            return FulfillmentMethods
        },
        isBackStepperDisabled()
        {
            if(this.viewData.isLoading)
                return true;

            return (this.viewData.currentStep === this.steps.flightData.value);
        },
        isForwardStepperDisabled()
        {
            if(this.viewData.isLoading)
                return true;

            switch (this.viewData.currentStep)
            {
                case (this.steps.flightData.value):
                    return !this.formValidity.isFlightDataValid;
                case (this.steps.additionalData.value):
                    return !this.formValidity.isAdditionalDataValid;
                case (this.steps.fulfillmentSelection.value):
                    return (!this.formValidity.isFulFilllmentSelectionValid || (this.formData.calculatedCompensation.scenario !== CompensationScenarios.COMPENSABLE.value));
                case (this.steps.personalData.value):
                    return !this.formValidity.isPersonalDataValid;
                case (this.steps.summaryPage.value):
                    return !this.formValidity.isSummaryValid;
                default:
                    return true;
            }
        },
        availableSteps() {
            let steps = [
                this.steps.flightData,
                this.steps.additionalData,
                this.steps.fulfillmentSelection
            ];

            if (this.formData.selectedFulfillmentMethod === FulfillmentMethods.SELF.value)
            {
                steps.push(this.steps.personalData);
                steps.push(this.steps.summaryPage);
            }
            else if (this.formData.selectedFulfillmentMethod !== null)
                steps.push(this.steps.redirectionPage);

            return steps;
        },
        stepperNextText() {
            if (this.viewData.currentStep === this.steps.summaryPage.value)
                return 'Abschicken';

            return 'Weiter';
        },
        selectedCaseType()
        {
            if(this.formData.flightData !== null)
                return this.formData.flightData.type;

            return null;
        },
        hasRSV()
        {
            return this.formData.hasRSV === '1';
        },
        hasStopover()
        {
            if(this.formData.flightData !== null)
                return this.formData.flightData.stopover !== null;

            return null;
        }
    },
    data: () => ({
        constants: {
            CASE_TYPE_FLIGHTS: 3
        },
        airlines: [],
        rsvs: [],
        formValidity: {
            isFlightDataValid: false,
            isAdditionalDataValid: false,
            isFulFilllmentSelectionValid: false,
            isPersonalDataValid: false,
            isSummaryValid: false
        },
        formData: {
            flightData: null,
            additionalData: null,
            selectedFulfillmentMethod: null,
            personalData: {
                mandator: {
                    lawform: null,
                    prename: null,
                    surname: null,
                    street: null,
                    house_number: null,
                    zip: null,
                    city: null,
                    mail: null,
                    phone: null,
                    iban: null,
                    bic: null
                },
                fellowPassengers: [],
                expenses: [],
                rsv_id: null
            },
            hasRSV: null,
            calculatedCompensation: {
                compensation: null,
                scenario: null
            },
            portalDataId: null
        },
        viewData: {
            currentStep: 1,
            isLoading: false,
            showErrorDialog: false,
            isErrorDialogClosable: true,
            showSubmitDialog: false
        },
        steps: {
            flightData: {
                value: 1,
                title: 'Flugdaten'
            },
            additionalData: {
                value: 2,
                title: 'Zusätzliche Daten'
            },
            fulfillmentSelection: {
                value: 3,
                title: 'Beauftragung'
            },
            redirectionPage: {
                value: 4,
                title: 'Weiterleitung'
            },
            personalData: {
                value: 4,
                title: 'Persönliche Daten'
            },
            summaryPage: {
                value: 5,
                title: 'Zusammenfassung'
            }
        },
        errorDetails: {
            title: null,
            message: null
        }
    }),
    methods:
    {
        stepForward()
        {
            switch (this.viewData.currentStep)
            {
                case this.steps.flightData.value:
                    this.stepToAdditionalData();
                    break;
                case this.steps.additionalData.value:
                    this.stepToFulfillmentSelection();
                    break;
                case this.steps.fulfillmentSelection.value:
                    this.goOnAfterFulfillmentSelection();
                    break;
                case this.steps.personalData.value:
                    this.stepToSummary();
                    break;
                case this.steps.summaryPage.value:
                    this.submitForm();
                    break;
            }

            this.scrollToTop();
        },
        stepBackward()
        {
            switch (this.viewData.currentStep)
            {
                case this.steps.additionalData.value:
                    this.viewData.currentStep = this.steps.flightData.value;
                    break;
                case this.steps.fulfillmentSelection.value:
                    this.viewData.currentStep = this.steps.additionalData.value;
                    break;
                case this.steps.personalData.value:
                    this.viewData.currentStep = this.steps.fulfillmentSelection.value;
                    break;
                case this.steps.summaryPage.value:
                    this.viewData.currentStep = this.steps.personalData.value;
                    break;
            }

            this.scrollToTop();
        },
        buildCaseData()
        {
            return {
                ...this.formData.flightData,
                ...this.formData.additionalData
            };
        },
        buildDataForCompensationCheck()
        {
            return {
                flightData: {
                    plannedRelevantDate: this.formData.flightData.planned_relevant_date,
                    actualRelevantDate: this.formData.flightData.actual_relevant_date,
                    type: this.formData.flightData.type,
                    airlineId: this.formData.flightData.main_airline_id,
                },
                startPosition: {
                    latitude: this.formData.flightData.start_latitude,
                    longitude: this.formData.flightData.start_longitude,
                    country: this.formData.flightData.start_country
                },
                endPosition: {
                    latitude: this.formData.flightData.end_latitude,
                    longitude: this.formData.flightData.end_longitude,
                    country: this.formData.flightData.end_country
                }
            };
        },
        checkForNonCompensationReason()
        {
            let caseData = this.buildCaseData();
            let noCompensationReason = null;
            let flightDate = moment(caseData.flightdate, 'YYYY-MM-DD').endOf('day');
            let limitationPeriodYears = 3;
            let sevenDays = 7;
            let fourteenDays = 14;
            let fourHours = 240;
            let twoHours = 120;

            if (caseData.cause === CauseTypes.CAUSE_GOD.value)
                noCompensationReason = CompensationScenarios.GOD.value;

            if (caseData.type === CaseTypes.CASE_TYPE_NO_BOARDING.value)
            {
                if (caseData.was_late_for_boarding === RadioButtonStringValues.YES.value)
                    noCompensationReason = CompensationScenarios.TOO_LATE.value;
                else if (caseData.was_boarding_lateness_acceptable === RadioButtonStringValues.YES.value)
                    noCompensationReason = CompensationScenarios.SICK_OR_UNSAFE.value;
            }

            let yearDiff = moment().endOf('day').diff(flightDate, 'years') < 0 ?
                -1 * moment().endOf('day').diff(flightDate, 'years') : moment().endOf('day').diff(flightDate, 'years');

            if (yearDiff > limitationPeriodYears)
                noCompensationReason = CompensationScenarios.LIMITATION_PERIOD.value;

            let flightDateMinuteDiff = moment(caseData.actual_relevant_date).diff(moment(caseData.planned_relevant_date), 'minutes') < 0 ?
                -1 * moment(caseData.actual_relevant_date).diff(moment(caseData.planned_relevant_date), 'minutes') : moment(caseData.actual_relevant_date).diff(moment(caseData.planned_relevant_date), 'minutes');

            if (caseData.change_notification_date)
            {
                let changeNotificationDate = moment(caseData.change_notification_date, 'YYYY-MM-DD').endOf('day');
                let flightDateNotificationDiff = flightDate.diff(changeNotificationDate, 'days') < 0 ?
                    -1 * flightDate.diff(changeNotificationDate, 'days') : flightDate.diff(changeNotificationDate, 'days');

                if(flightDateNotificationDiff > fourteenDays)
                    noCompensationReason = CompensationScenarios.GOT_CHANGE_INFORMATION_ON_TIME.value;
                else if(flightDateNotificationDiff > sevenDays)
                {
                    if(caseData.type === CaseTypes.CASE_TYPE_CANCELED.value)
                    {
                        if(flightDateMinuteDiff <= fourHours)
                            noCompensationReason = CompensationScenarios.GOT_CHANGE_INFORMATION_ON_TIME_CANCELLED.value;
                    }
                }
                else if(caseData.type === CaseTypes.CASE_TYPE_CANCELED.value && flightDateMinuteDiff <= twoHours)
                            noCompensationReason = CompensationScenarios.GOT_CHANGE_INFORMATION_ON_TIME_CANCELLED_LATE.value;
            }
            else if(caseData.type === CaseTypes.CASE_TYPE_CANCELED.value && flightDateMinuteDiff <= twoHours)
                    noCompensationReason = CompensationScenarios.GOT_CHANGE_INFORMATION_ON_TIME_CANCELLED_LATE.value;

            return noCompensationReason;
        },
        async stepToFulfillmentSelection()
        {
            let noCompensationReason = this.checkForNonCompensationReason();

            if(noCompensationReason)
            {
                this.formData.calculatedCompensation.scenario = noCompensationReason;
                this.viewData.currentStep = this.steps.fulfillmentSelection.value;
            }
            else
            {
                this.viewData.isLoading = true;

                try
                {
                    this.formData.calculatedCompensation = await calculateCompensation(this.buildDataForCompensationCheck());
                    this.viewData.currentStep = this.steps.fulfillmentSelection.value;
                }
                catch(error)
                {
                    this.errorDetails.title         = 'Es tut uns Leid!';
                    this.errorDetails.message       = 'Bei der Prüfung deiner Flugdaten ist folgender Fehler aufgetreten: ' + error;
                    this.viewData.showErrorDialog   = true;
                }
                finally
                {
                    this.viewData.isLoading = false;
                }
            }
        },
        stepToAdditionalData()
        {
            this.viewData.currentStep = this.steps.additionalData.value;
        },
        stepToPersonalData()
        {
            this.viewData.currentStep = this.steps.personalData.value;
        },
        async stepToRedirectionPage()
        {
            this.viewData.isLoading = true;

            try
            {
                let caseData = {...this.buildCaseData(), ...{selected_fulfillment_method: this.formData.selectedFulfillmentMethod, has_rsv: this.formData.hasRSV}};

                this.formData.portalDataId = await createUnfinishedCase(this.constants.CASE_TYPE_FLIGHTS, caseData);

                this.viewData.currentStep = this.steps.redirectionPage.value;

                setTimeout(function(portalDataId){window.location.href = Config.PORTAL_URL + '/redirected/flight/' + portalDataId}, 3000, this.formData.portalDataId);
            }
            catch (error)
            {
                this.errorDetails.title         = 'Es tut uns Leid!';
                this.errorDetails.message       = 'Beim Zwischenspeichern des Falls ist folgender Fehler aufgetreten: ' + error;
                this.viewData.showErrorDialog   = true;
            }
            finally
            {
                this.viewData.isLoading = false;
            }
        },
        stepToSummary()
        {
            this.viewData.currentStep = this.steps.summaryPage.value;
        },
        goOnAfterFulfillmentSelection()
        {
            if(this.formData.selectedFulfillmentMethod === FulfillmentMethods.SELF.value)
                this.stepToPersonalData();
            else
                this.stepToRedirectionPage();
        },
        onFlightDataValidityChange(isValid)
        {
            this.formValidity.isFlightDataValid = isValid;
        },
        onFlightDataChange(flightData)
        {
            this.formData.flightData = flightData;
        },
        onAdditionalDataValidityChange(isValid)
        {
            this.formValidity.isAdditionalDataValid = isValid;
        },
        onAdditionalDataChange(flightData)
        {
            this.formData.additionalData = flightData;
        },
        onFulfillmentSelectionValidityChange(isValid)
        {
            this.formValidity.isFulFilllmentSelectionValid = isValid;
        },
        onFulfillmentSelectionChange(selectedFulfillmentMethod)
        {
            this.formData.selectedFulfillmentMethod = selectedFulfillmentMethod;
        },
        onPersonalDataValidityChange(isValid)
        {
            this.formValidity.isPersonalDataValid = isValid;
        },
        onPersonalDataChange(selectedFulfillmentMethod)
        {
            this.formData.personalData = selectedFulfillmentMethod;
        },
        onSummaryValidityChange(isValid)
        {
            this.formValidity.isSummaryValid = isValid;
        },
        onHasRSVValueChanged(hasRSV)
        {
            this.formData.hasRSV = hasRSV;
        },
        scrollToTop()
        {
            this.$refs.stepperItems.scrollIntoView(true);
        },
        showApplicationIsUnavailableError()
        {
            this.viewData.isErrorDialogClosable = false;
            this.viewData.showErrorDialog = true;
            this.errorDetails.title = 'Es tut uns Leid!';
            this.errorDetails.message = 'Die Anwendung steht aktuell nicht zur Verfügung. Bitte versuch es später noch einmal.'
        },
        async submitForm()
        {
            this.viewData.showSubmitDialog = true;

            try
            {
                this.viewData.isLoading = true;
                await createNewCase({});
                this.viewData.isLoading = false;
            }
            catch(error)
            {
                this.viewData.isLoading = false;
                this.viewData.showSubmitDialog = false;

                this.errorDetails.title = 'Es tut uns Leid!';
                this.errorDetails.message = 'Beim Übertragen deiner Daten ist ein Fehler passiert. Bitte versuchs nochmal. '
                    + 'Sollte der Fehler auch nach mehrmaligen Versuchen weiterhin auftreten, kontaktiere uns bitte unter der Angabe des Fehlers: '
                    + error;

                this.viewData.showErrorDialog = true;
            }

            // try
            // {
            //     let requestData = {
            //         caseData: this.formData.caseData,
            //         mandator: this.formData.personalData.mandator,
            //         fellowPassengers: this.formData.personalData.fellowPassengers,
            //         expenses: this.formData.personalData.expenses,
            //         fulfillmentMethod: this.formData.selectedFulfillmentMethod
            //     };
            //
            //     await createNewCase(requestData);
            // }
            // catch (error)
            // {
            //     console.log(error);
            //     // this.errorInformation.generalErrorMessage = 'Fehler beim Versenden der Mail bzw. der Anlage des Falles: ' + error;
            // }
        },
        async checkHealth()
        {
            try
            {
                let response = await checkHealth();

                if (response.is_down)
                    this.showApplicationIsUnavailableError();
            }
            catch (error)
            {
                this.showApplicationIsUnavailableError();
            }
        }
    },
    async created() {
        await this.checkHealth();

        if(this.viewData.showErrorDialog === false)
        {
            this.airlines = await getAirlines();
            this.rsvs = await getRsvs();
        }
    }
}
</script>

<style scoped>

</style>
